import { Question, ResponsePayload } from '../types';
import { isMultipleSelectSelectionCountValid } from './multipleSelectLimits';
import { normalizeQuestionType } from './questionTypeUtils';

export interface HorizontalDragValidationState {
  leftIds: number[];
  middleIds: number[];
  rightIds: number[];
  correctColumnById: Record<number, 'left' | 'right'>;
}

export interface QuestionResponseValidationContext {
  draft?: ResponsePayload | null;
  selectedOptions?: number[];
  customOtherValue?: string | null;
  textValue?: string | null;
  booleanValue?: boolean | null;
  ratingValue?: number | null;
  audioBlob?: Blob | null;
  audioUrl?: string | null;
  isPdfValid?: boolean;
  isVideoResponseValid?: boolean;
  textListValue?: string[] | null;
  horizontalDragState?: HorizontalDragValidationState;
}

const backendOnlyTypes = [
  'expandable_list',
  'short_write_in',
  'title',
  'image_as_button',
  'fill_in_the_blank',
];

const autoProgressTypes = new Set([
  'description',
  'image',
  'multiple_reveal',
  'accordion',
  'additional_resources',
  'radar_chart',
  'ai_summary',
  'expandable_list',
  'short_write_in',
  'title',
  'image_as_button',
  'fill_in_the_blank',
  'video_player',
  'pdf',
]);


const getTrimmed = (value?: string | null) => (value ?? '').trim();

const resolveSelectedOptions = (context?: QuestionResponseValidationContext) => {
  if (Array.isArray(context?.selectedOptions)) {
    return context!.selectedOptions;
  }
  if (Array.isArray(context?.draft?.selected_options)) {
    return context!.draft!.selected_options as number[];
  }
  return [];
};

const resolveCustomOtherValue = (context?: QuestionResponseValidationContext) =>
  getTrimmed(context?.customOtherValue ?? context?.draft?.custom_other_value ?? null);

const resolveTextValue = (context?: QuestionResponseValidationContext) =>
  getTrimmed(context?.textValue ?? context?.draft?.text_value ?? null);

const resolveTextListValue = (context?: QuestionResponseValidationContext) => {
  const direct = context?.textListValue;
  if (Array.isArray(direct)) {
    return direct.map((item) => (typeof item === 'string' ? item.trim() : String(item ?? '').trim()));
  }
  const draftList = context?.draft?.text_list;
  if (Array.isArray(draftList)) {
    return draftList.map((item) => (typeof item === 'string' ? item.trim() : String(item ?? '').trim()));
  }
  return [] as string[];
};

const resolveBooleanValue = (context?: QuestionResponseValidationContext) => {
  if (typeof context?.booleanValue === 'boolean') {
    return context.booleanValue;
  }
  if (typeof context?.draft?.boolean_value === 'boolean') {
    return context.draft.boolean_value;
  }
  return null;
};

const resolveRatingValue = (context?: QuestionResponseValidationContext) => {
  const value = context?.ratingValue ?? context?.draft?.rating_value;
  return typeof value === 'number' ? value : null;
};

const hasAudioResponse = (context?: QuestionResponseValidationContext) => {
  if (context?.audioBlob) {
    return true;
  }
  if (context?.audioUrl && context.audioUrl.trim().length > 0) {
    return true;
  }
  const url = context?.draft?.audio_url;
  return typeof url === 'string' && url.trim().length > 0;
};

const optionRequiresCustomValue = (question: Question, selected: number[]) => {
  const otherOption = question.options?.find((option) => option.is_other);
  if (!otherOption) {
    return false;
  }
  return selected.includes(otherOption.id);
};

export const isQuestionResponseValid = (
  question?: Question | null,
  context?: QuestionResponseValidationContext,
): boolean => {
  if (!question) {
    return false;
  }

  const type = normalizeQuestionType(question.question_type_display ?? question.question_type ?? '');
  const selectedOptions = resolveSelectedOptions(context);
  const customOtherValue = resolveCustomOtherValue(context);
  const textValue = resolveTextValue(context);
  const booleanValue = resolveBooleanValue(context);
  const ratingValue = resolveRatingValue(context);
  const textList = resolveTextListValue(context);

  switch (type) {
    case 'description':
    case 'image':
    case 'multiple_reveal':
    case 'accordion':
    case 'additional_resources':
    case 'radar_chart':
    case 'ai_summary':
      return true;
    case 'multiple_choice':
      if (
        selectedOptions.length !== 1 ||
        !question.options?.some((option) => option.id === selectedOptions[0])
      ) {
        return false;
      }
      if (optionRequiresCustomValue(question, selectedOptions)) {
        return customOtherValue.length > 0;
      }
      return true;
    case 'multiple_select':
      if (!selectedOptions.length) {
        return false;
      }
      if (!isMultipleSelectSelectionCountValid(selectedOptions.length, question.metadata)) {
        return false;
      }
      if (optionRequiresCustomValue(question, selectedOptions)) {
        return customOtherValue.length > 0;
      }
      return true;
    case 'drag_and_drop':
      if (!selectedOptions.length) {
        return false;
      }
      if (optionRequiresCustomValue(question, selectedOptions)) {
        return customOtherValue.length > 0;
      }
      return true;
    case 'drag_and_drop_ranking': {
      const optionIds = Array.isArray(question.options)
        ? question.options
            .map((option) => option?.id)
            .filter((id): id is number => typeof id === 'number')
        : [];

      if (!optionIds.length) {
        return false;
      }

      if (selectedOptions.length !== optionIds.length) {
        return false;
      }

      const optionSet = new Set(optionIds);
      const seen = new Set<number>();

      for (const id of selectedOptions) {
        if (!optionSet.has(id) || seen.has(id)) {
          return false;
        }
        seen.add(id);
      }

      return true;
    }
    case 'horizontal_drag_drop': {
      const dragState = context?.horizontalDragState;

      if (dragState) {
        const { leftIds, middleIds, rightIds, correctColumnById } = dragState;
        const trackedIds = Object.keys(correctColumnById ?? {});

        if (!trackedIds.length) {
          return false;
        }

        if (middleIds.length > 0) {
          return false;
        }

        if (leftIds.length + rightIds.length !== trackedIds.length) {
          return false;
        }

        const leftValid = leftIds.every((id) => correctColumnById[id] === 'left');
        const rightValid = rightIds.every((id) => correctColumnById[id] === 'right');

        if (!leftValid || !rightValid) {
          return false;
        }

        if (optionRequiresCustomValue(question, rightIds)) {
          return customOtherValue.length > 0;
        }

        return true;
      }

      if (!selectedOptions.length) {
        return false;
      }
      if (optionRequiresCustomValue(question, selectedOptions)) {
        return customOtherValue.length > 0;
      }
      return true;
    }
    case 'boolean':
      return typeof booleanValue === 'boolean';
    case 'text_or_audio':
      return hasAudioResponse(context) || textValue.length > 0;
    case 'text_list': {
      return textList.length > 0 && textList.every((item) => item.length > 0);
    }
    case 'rating':
      return typeof ratingValue === 'number' && ratingValue >= 1 && ratingValue <= 7;
    case 'video_player':
      if (typeof context?.isVideoResponseValid === 'boolean') {
        return context.isVideoResponseValid;
      }
      return Boolean(context?.draft);
    case 'pdf':
      if (typeof context?.isPdfValid === 'boolean') {
        return context.isPdfValid;
      }
      return Boolean(context?.draft);
    case 'callback_summary':
      return textValue.length > 0;
    case 'custom_screen':
      return context?.draft?.is_completed === true;
    case 'roleplay':
      // Completion is recorded server-side once the evaluator confirms a win.
      return context?.draft?.is_completed === true;
    default:
      if (backendOnlyTypes.includes(type)) {
        return true;
      }
      return Boolean(context?.draft);
  }
};

export const isQuestionAutoProgress = (question?: Question | null): boolean => {
  if (!question) {
    return false;
  }
  const type = normalizeQuestionType(question.question_type_display ?? question.question_type ?? '');
  return autoProgressTypes.has(type);
};

